"use client";

import { ChatList } from "@/components/AI/chat/ChatList";
import { ChatPanel } from "@/components/AI/chat/ChatPanel";
import {
  getAiMessageFromErrors,
  useJoinedErrors,
} from "@/components/Canvas/hooks/useStoryboardErrors";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  useAiActionStore,
  useCurrentConvo,
  AI_MODEL,
  AI_AGENT,
} from "@/store/zustand/aiActionStore";
import {
  useAiContextStore,
  useCurrentMessagesBeingShown,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { Message, useSupabaseStore } from "@/store/zustand/supabaseStore";
import { track } from "@/utils/events/tracker";
import { countSearchMatches, createSearchRegex } from "@/utils/searchUtils";
import { useCanvasId } from "@/utils/utils";
import { HistoryIcon, PlusIcon, SearchIcon, XIcon } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import TopUsageIndicator from "./TopUsageIndicator";
import { useSendMessage } from "./useSendMessage";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import useClaudeCodeSessions from "@/hooks/useClaudeCodeSessions";
import { useSendClaudeCodeMessage } from "@/hooks/useSendClaudeCodeMessage";
import { PendingQuestionsProvider } from "./contexts/PendingQuestionsContext";
import { ToolResultsProvider } from "./contexts/ToolResultsContext";

export interface ChatProps extends React.ComponentProps<"div"> {
  agent?: AI_AGENT;
}

/**
 * Chat component wrapped with PendingQuestionsProvider.
 * The provider must wrap the component so useSendMessage can access the context.
 */
export const Chat = React.memo(({ className, agent: agentProp }: ChatProps) => {
  return (
    <PendingQuestionsProvider>
      <ChatInner className={className} agent={agentProp} />
    </PendingQuestionsProvider>
  );
});
Chat.displayName = "Chat";

const ChatInner = React.memo(({ className, agent: agentProp }: ChatProps) => {
  const canvasId = useCanvasId();
  const location = useLocation();

  const isHomeRoute = location.pathname === "/";

  const setShowUpgradeModal = usePlanInfoStore(
    (state) => state.setShowUpgradeModal,
  );

  const { conversation, messages: messagesFromConvo } = useCurrentConvo();
  const selectedConversationId = useAiActionStore(
    (state) => state.selectedConversationId,
  );

  // Ai action store
  const chatOnlyMode = useAiContextStore((state) => state.chatOnlyMode);
  const useReasoning = useAiContextStore((state) => state.useReasoning);
  const useSearch = useAiContextStore((state) => state.useSearch);

  // Triggering sending message from anywhere in the app
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage,
  );

  const sendChatMessage = useAiContextStore((state) => state.sendChatMessage);
  const sendChatMessageFiles = useAiContextStore(
    (state) => state.sendChatMessageFiles,
  );
  const refreshAfterResponse = useAiContextStore(
    (state) => state.refreshAfterResponse,
  );
  const includeErrorsIfAny = useAiContextStore(
    (state) => state.includeErrorsIfAny,
  );
  const isFreePromptSendMessage = useAiContextStore(
    (state) => state.isFreePrompt,
  );
  const additionalInstructionsFromStore = useAiContextStore(
    (state) => state.additionalInstructions,
  );
  const agentChoiceFromStore = useAiContextStore((state) => state.agentChoice);
  const criticalFilesFromStore = useAiContextStore(
    (state) => state.criticalFiles,
  );

  const codeGenerationModel = useAiActionStore(
    (state) => state.codeGenerationModel,
  );
  const agentChoiceFromActionStore = useAiActionStore((state) => state.agent);
  const setAgent = useAiActionStore((state) => state.setAgent);

  // Use prop if provided, otherwise fall back to store value
  const agentChoice = agentProp ?? agentChoiceFromActionStore;

  // Add to DB
  const manualMessageToAdd = useAiContextStore(
    (state) => state.manualMessageToAdd,
  );

  const conversations = useSupabaseStore((state) => state.aiChatConversations);
  const conversationsStatus = useSupabaseStore(
    (state) => state.aiChatConversationsStatus,
  );

  // Manually adding an assistant message
  // Temporary - while streaming in
  const allMessagesToShow = useCurrentMessagesBeingShown();

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const allAiMessages = useSupabaseStore((state) => state.aiChatMessages);

  // Fetch Claude Code sessions
  const {
    handleClaudeCodeSessionSelect,
    isClaudeCodeSession,
    loadClaudeCodeMessages,
  } = useClaudeCodeSessions(canvasId);

  const { sendClaudeCodeMessage } = useSendClaudeCodeMessage(
    canvasId,
    loadClaudeCodeMessages,
  );

  // UNIFIED conversation list - Includes both Supabase and Claude Code conversations from store
  const conversationsListForCanvas = useMemo(() => {
    return Object.values(conversations)
      .filter((c) => c.canvas_id === canvasId)
      .map((c) => {
        const lastMessage = Object.values(allAiMessages)
          .filter((m) => m.conversation_id === c.id)
          .sort((a: any, b: any) => {
            return (
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
            );
          })[0];

        return {
          ...c,
          lastMessage: lastMessage || {
            created_at: c.created_at,
            content: isClaudeCodeSession(c.id) ? "Claude Code Chat" : "",
          },
        };
      })
      .sort((a, b) => {
        // Sort by last message timestamp (most recent first)
        const aTime = new Date(
          a.lastMessage?.created_at || a.created_at,
        ).getTime();
        const bTime = new Date(
          b.lastMessage?.created_at || b.created_at,
        ).getTime();
        return bTime - aTime;
      });
  }, [conversations, allAiMessages, canvasId]);

  const allErrors = useJoinedErrors();

  // Use useRef instead of useState to avoid unnecessary rerenders
  // and potential race conditions with Set modifications
  const errorsLoggedRef = useRef(new Set<string>());

  useEffect(() => {
    if (selectedConversationId) {
      useSupabaseStore.getState().resetMessages(selectedConversationId);
    }
  }, [selectedConversationId]);

  // Reset the errors logged when the errors are cleared
  useEffect(() => {
    if (!allErrors?.length) {
      errorsLoggedRef.current.clear();
    }
  }, [allErrors]);

  // Log all errors in posthog, but only when the error messages/stack change
  const uniquenessCheck = useMemo(() => {
    return allErrors.map((error) => error.message + error.stack).join(",");
  }, [allErrors]);

  useEffect(() => {
    if (allErrors?.length) {
      allErrors.forEach((error) => {
        const errorCheck = error.message + error.stack;
        if (errorsLoggedRef.current.has(errorCheck)) {
          return;
        }

        errorsLoggedRef.current.add(errorCheck);
        track("ERROR_IN_CHAT", {
          error: error,
        });
      });
    }
  }, [uniquenessCheck]); // eslint-disable-line react-hooks/exhaustive-deps

  const createNewConvo = async () => {
    if (!selectedConversationId || !allMessagesToShow?.length) {
      return;
    }

    const newConversation = {
      id: uuidv4().toString(),
      canvas_id: canvasId,
      created_at: new Date().toISOString(),
      // Add ai_provider_session_id for Claude Agent conversations
      ...(agentChoice === AI_AGENT.CLAUDE_AGENT && {
        ai_provider_session_id: "claude_code",
      }),
    };

    useSupabaseStore.getState().onDbSnapshot("aiChatConversations", {
      newItems: [newConversation],
    });

    // @ts-expect-error
    await supabaseClient
      .from("ai_chat_conversations")
      .insert([newConversation]);

    useAiActionStore.getState().setSelectionConversationId(newConversation.id);
  };

  // Handle Claude Code conversation selection (STORE-ONLY, NO DATABASE)
  const handleClaudeCodeConversationSelect = async (conversationId: string) => {
    try {
      useAiActionStore.getState().setSelectionConversationId(conversationId);

      // Load messages (STORE-ONLY, NO DATABASE)
      await handleClaudeCodeSessionSelect(conversationId);
    } catch (error) {
      console.error("Failed to load Claude Code conversation:", error);
    }
  };

  useEffect(() => {
    const addMessageToDb = async () => {
      let fullTextContent = manualMessageToAdd?.text || "";
      if (manualMessageToAdd?.progressItems?.length) {
        fullTextContent +=
          '\n<action-progress>\n{"items":' +
          JSON.stringify(manualMessageToAdd?.progressItems) +
          "}\n</action-progress>";
      }

      if (manualMessageToAdd?.afterText) {
        fullTextContent += `\n${manualMessageToAdd?.afterText}`;
      }

      const newMessage: any = {
        id: manualMessageToAdd?.id || uuidv4().toString(),
        conversation_id: selectedConversationId,
        canvas_id: canvasId,
        content: fullTextContent,
        role: "assistant",
        message_context: null,
        tool_calls: null,
        tool_call_id: null,
        created_at: new Date().toISOString(),
      };

      useSupabaseStore.getState().onDbSnapshot("aiChatMessages", {
        newItems: [newMessage],
      });

      // @ts-expect-error
      await supabaseClient.from("ai_chat_messages").insert(newMessage);
    };

    if (manualMessageToAdd && selectedConversationId) {
      useAiContextStore.getState().setAddManualMessage(null);
      addMessageToDb();
    }
  }, [manualMessageToAdd, selectedConversationId, canvasId, supabaseClient]);

  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchInput, setShowSearchInput] = useState(false);

  // Filter messages based on search query
  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return allMessagesToShow;

    // Use the createSearchRegex utility function
    const { regex } = createSearchRegex(searchQuery) || { regex: null };
    if (!regex) return allMessagesToShow;

    const filtered =
      allMessagesToShow?.filter((message) => {
        if (!message?.content) return false;
        // Test if the message contains the search query
        return regex.test(message.content);
      }) || [];

    return filtered;
  }, [allMessagesToShow, searchQuery]);

  // Reset search when changing conversations
  useEffect(() => {
    setSearchQuery("");
    setShowSearchInput(false);
  }, [conversation?.id]);

  /**
   * If there are no conversations, create a new one
   */
  useEffect(() => {
    if (isHomeRoute || !supabaseClient) {
      return;
    }

    if (
      !conversationsListForCanvas?.length &&
      conversationsStatus === "succeeded"
    ) {
      // Create a new conversation
      const createConvo = async () => {
        const newConversation = {
          id: uuidv4(),
          canvas_id: canvasId,
          created_at: new Date().toISOString(), // Add explicit timestamp
          // Add ai_provider_session_id for Claude Agent conversations
          ...(agentChoice === AI_AGENT.CLAUDE_AGENT && {
            ai_provider_session_id: "claude_code",
          }),
        };

        // Double check there are no conversations
        const { data: existingConversations, error } = await supabaseClient
          .from("ai_chat_conversations")
          .select("id")
          .eq("canvas_id", canvasId);

        if (error || existingConversations?.length) {
          return;
        }

        await supabaseClient
          .from("ai_chat_conversations")
          .insert([newConversation]);

        useSupabaseStore.getState().onDbSnapshot("aiChatConversations", {
          newItems: [newConversation],
        });
      };

      createConvo();
    }
  }, [
    conversationsListForCanvas,
    conversationsStatus,
    supabaseClient,
    canvasId,
    isHomeRoute,
    agentChoice,
  ]);

  /**
   * Select the last conversation on first load and handle Claude Code conversations
   */
  useEffect(() => {
    if (!selectedConversationId && conversationsListForCanvas?.length) {
      // If agent is Claude Agent, prioritize Claude Code conversations
      // Otherwise, prioritize regular conversations
      let selectedConversation = conversationsListForCanvas[0]; // Default to first (most recent)

      if (agentChoice === AI_AGENT.CLAUDE_AGENT) {
        // Look for the most recent Claude Code conversation
        const claudeCodeConversation = conversationsListForCanvas.find((conv) =>
          isClaudeCodeSession(conv.id),
        );
        if (claudeCodeConversation) {
          selectedConversation = claudeCodeConversation;
        }
      } else {
        // Look for the most recent regular conversation
        const regularConversation = conversationsListForCanvas.find(
          (conv) => !isClaudeCodeSession(conv.id),
        );
        if (regularConversation) {
          selectedConversation = regularConversation;
        }
      }

      if (isClaudeCodeSession(selectedConversation.id)) {
        handleClaudeCodeConversationSelect(selectedConversation.id).catch(
          (error) => {
            console.error(
              "Failed to load Claude Code conversation on first load:",
              error,
            );
          },
        );
      } else {
        // Regular conversation - just select it
        useAiActionStore
          .getState()
          .setSelectionConversationId(selectedConversation.id);
      }
    }
  }, [
    conversationsListForCanvas,
    selectedConversationId,
    isClaudeCodeSession,
    handleClaudeCodeConversationSelect,
    agentChoice,
    setAgent,
  ]);

  const isChatLoading = useIsChatLoading();

  const { sendMessage } = useSendMessage();

  /**
   * Switch conversations when agent changes
   */
  useEffect(() => {
    if (!conversationsListForCanvas?.length) return;

    // When switching to Claude Agent, switch to most recent Claude Code conversation
    if (agentChoice === AI_AGENT.CLAUDE_AGENT) {
      const claudeCodeConversation = conversationsListForCanvas.find((conv) =>
        isClaudeCodeSession(conv.id),
      );

      // Only switch if currently selected conversation is NOT a Claude Code session
      if (
        claudeCodeConversation &&
        selectedConversationId &&
        !isClaudeCodeSession(selectedConversationId)
      ) {
        handleClaudeCodeConversationSelect(claudeCodeConversation.id).catch(
          (error) => {
            console.error(
              "Failed to switch to Claude Code conversation:",
              error,
            );
          },
        );
      }
    }
    // When switching away from Claude Agent, switch to most recent regular conversation
    else if (
      selectedConversationId &&
      isClaudeCodeSession(selectedConversationId)
    ) {
      const regularConversation = conversationsListForCanvas.find(
        (conv) => !isClaudeCodeSession(conv.id),
      );

      if (regularConversation) {
        useAiActionStore
          .getState()
          .setSelectionConversationId(regularConversation.id);
      }
    }
  }, [agentChoice]); // Only trigger when agent changes

  useEffect(() => {
    if (!conversation) return;

    if (sendChatMessage) {
      setSendChatMessage({
        // @ts-expect-error TS2322
        sendChatMessage: null,
      });

      // Check if current conversation is a Claude Code session
      if (
        selectedConversationId &&
        isClaudeCodeSession(selectedConversationId)
      ) {
        // Use Claude Code API for sending messages to Claude Code conversations
        sendClaudeCodeMessage({
          message: sendChatMessage,
          conversationId: selectedConversationId,
          attachedFiles: sendChatMessageFiles,
          includeErrorsIfAny,
          allErrors,
          refreshAfterResponse,
          additionalInstructions: additionalInstructionsFromStore,
        }).catch((error) => {
          console.error("Failed to send Claude Code message:", error);
          // TODO: Show error toast to user
        });
      } else {
        // Use regular Tempo API for sending messages
        const messagesCanSummarize: Message[] = [...(messagesFromConvo || [])];
        const messagesDoNotSummarize: Message[] = [];

        if (includeErrorsIfAny && allErrors?.length) {
          const errorsMessage = getAiMessageFromErrors(allErrors);

          const errorMessage: any = {
            id: uuidv4().toString(),
            conversation_id: selectedConversationId,
            canvas_id: canvasId,
            content: errorsMessage.content,
            role: "user",
            message_context: null,
            tool_calls: null,
            tool_call_id: null,
            created_at: new Date().toISOString(),
          };

          useSupabaseStore.getState().onDbSnapshot("aiChatMessages", {
            newItems: [errorMessage],
          });

          const addToDb = async () => {
            // @ts-expect-error
            await supabaseClient.from("ai_chat_messages").insert(errorMessage);
          };

          addToDb();

          messagesDoNotSummarize.push(errorMessage);
        }

        sendMessage({
          userPrompt: sendChatMessage,
          sendChatMessageFiles,
          refreshAfterResponse,
          disableTextEditor: false,
          messagesCanSummarize,
          messagesDoNotSummarize,
          additionalInstructions: additionalInstructionsFromStore,
          criticalFiles: criticalFilesFromStore || [],
          chatOnlyMode: chatOnlyMode,
          isFreePrompt: isFreePromptSendMessage,
          useReasoning: useReasoning,
          useSearch: useSearch,
          codeGenerationModel: codeGenerationModel,
          agentChoice: agentChoiceFromStore || agentChoice,
        });
      }
    }
  }, [
    sendChatMessage,
    conversation,
    agentChoice,
    selectedConversationId,
    isClaudeCodeSession,
    sendClaudeCodeMessage,
  ]);

  const searchResultText = useMemo(() => {
    if (!searchQuery) return ""; // Return empty if no search query
    const totalMatches = countSearchMatches(filteredMessages, searchQuery);
    return `Found ${totalMatches} matches in ${
      filteredMessages.length
    } message${filteredMessages.length !== 1 ? "s" : ""}`;
  }, [filteredMessages, searchQuery]);

  return (
    <div className={cn("relative h-full flex flex-col max-h-full", className)}>
      <div className="flex flex-row items-center p-3 pl-2 h-12 border-b gap-2">
        <div
          className="flex flex-row items-center justify-start min-w-0 shrink"
          onClick={() => {
            setShowUpgradeModal(true);
          }}
        >
          <TopUsageIndicator />
        </div>
        <div
          className={cn(
            "flex gap-2 flex-row items-center text-xs grow-0 relative w-full justify-end",
          )}
        >
          <div
            className={`${
              showSearchInput ? "opacity-100" : "opacity-0 pointer-events-none"
            } transition-opacity duration-150 grow`}
          >
            <Input
              type="text"
              className="h-7 rounded border border-border-primary text-xs focus:outline-hidden focus:ring-1 focus:ring-accent"
              placeholder="Search chat..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus={showSearchInput}
            />
          </div>
          <SearchIcon
            className={cn(
              "size-3.5 hover:text-text-secondary text-text-muted cursor-pointer shrink-0",
              isHomeRoute && "hidden",
              isChatLoading && "opacity-50 pointer-events-none cursor-default",
              showSearchInput && "text-accent hidden",
            )}
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content="Search Chat"
            onClick={() => setShowSearchInput(!showSearchInput)}
          />
          <XIcon
            className={cn(
              "size-4 hover:text-text-secondary text-text-muted cursor-pointer shrink-0",
              showSearchInput ? "text-accent block" : "hidden",
            )}
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content="Search Chat"
            onClick={() => {
              setSearchQuery("");
              setShowSearchInput(!showSearchInput);
            }}
          />
          <PlusIcon
            className={cn(
              "size-3.5 hover:text-text-secondary text-text-muted cursor-pointer transition-all duration-200 shrink-0",
              isHomeRoute && "hidden",
              isChatLoading && "opacity-50 pointer-events-none cursor-default",
              showSearchInput ? "hidden" : "block",
            )}
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content="New Chat"
            onClick={createNewConvo}
          />
          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              className={cn(
                isHomeRoute && "hidden",
                showSearchInput ? "hidden" : "block",
              )}
              disabled={isChatLoading}
            >
              <HistoryIcon
                className={cn(
                  "size-3.5 hover:text-text-secondary text-text-muted cursor-pointer shrink-0",
                  isChatLoading &&
                    "opacity-50 pointer-events-none cursor-default",
                )}
                data-tooltip-id="tooltip"
                data-tooltip-place="bottom"
                data-tooltip-content="Chat History"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 mt-8" side={"right"}>
              <DropdownMenuLabel>History</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup className="w-full max-h-40 overflow-y-auto">
                {conversationsListForCanvas.map((conversation) => {
                  // Check if the timestamp is valid before trying to format it
                  const timestamp = conversation.created_at;
                  let formattedDate = "New conversation";

                  if (timestamp) {
                    try {
                      const date = new Date(timestamp);

                      // Make sure the date is valid
                      if (!isNaN(date.getTime())) {
                        const optionsDate: Intl.DateTimeFormatOptions = {
                          month: "short",
                          day: "numeric",
                        };
                        const optionsTime: Intl.DateTimeFormatOptions = {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        };
                        formattedDate = `${date.toLocaleDateString(
                          "en-US",
                          optionsDate,
                        )}, ${date.toLocaleTimeString("en-US", optionsTime)}`;
                      }
                    } catch (e) {
                      console.error("Error formatting date:", e);
                    }
                  }

                  return (
                    <DropdownMenuItem
                      key={conversation.id}
                      onClick={() => {
                        if (isClaudeCodeSession(conversation.id)) {
                          handleClaudeCodeConversationSelect(conversation.id);
                        } else {
                          useAiActionStore
                            .getState()
                            .setSelectionConversationId(conversation.id);
                        }
                      }}
                    >
                      <div className="flex flex-row items-center gap-2 w-full">
                        <div className="shrink-0">{formattedDate}</div>
                        <div className="truncate text-secondary-800 min-w-0 w-0 grow">
                          {conversation.lastMessage?.content}
                        </div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className={cn("grow min-h-0")}>
        {allMessagesToShow?.length ? (
          <>
            {searchQuery && (
              <div className="text-xs text-text-secondary px-4 py-2 bg-bg-secondary border-b border-border-primary">
                {filteredMessages.length === 0
                  ? "No messages found"
                  : searchResultText}
              </div>
            )}
            {(!searchQuery || filteredMessages.length > 0) && (
              <ToolResultsProvider messages={allMessagesToShow || []}>
                <ChatList
                  messages={searchQuery ? filteredMessages : allMessagesToShow}
                  conversationId={conversation?.id}
                  searchQuery={searchQuery}
                />
              </ToolResultsProvider>
            )}
          </>
        ) : null}
      </div>
      <div className="relative">
        <ChatPanel agent={agentChoice} />
      </div>
    </div>
  );
});
ChatInner.displayName = "ChatInner";
